import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { GLViewer } from 'domains/3d/GLViewer';
import { ViewerControls } from './ViewerControls';

const ViewerContainer = styled.div`
  width: 100%;
  min-height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  #buttons-container {
    display: flex;
    gap: 10px;
    padding: 10px;
    z-index: 10;
  }

  .viewer-button {
    background: rgba(255, 255, 255, 0.8);
    border-radius: 6px;
    padding: 5px;
    display: flex;
    flex-direction: row;
    gap: 5px;
    align-items: center;
    justify-content: center;
  }

  .viewer-button img {
    width: 32px;
    height: 32px;
    cursor: pointer;
  }
`;

type Props = {
  jsonString: string;
  params?: Partial<ConstructorParameters<typeof GLViewer>[1]>;
};

const GLViewerWrapper: React.FC<Props> = ({ jsonString, params }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<GLViewer | null>(null);
  const [viewerReady, setViewerReady] = useState(false);

  useEffect(() => {
    const container = containerRef.current;

    if (container && jsonString) {
      try {
        const model = JSON.parse(jsonString);
        const viewer = new GLViewer(
          model,
          {
            buttons: false,
            color: true,
            view: 'FRONT',
            ...params,
          },
          container,
        );

        container.appendChild(viewer.renderer.domElement);
        setViewerReady(true);
        viewerRef.current = viewer;
      } catch (err) {
        throw new Error(`Invalid JSON model: ${err}`);
      }
    }
    // cleanup
    return () => {
      container?.replaceChildren();
      viewerRef.current = null;
    };
  }, [jsonString, params]);

  return (
    <ViewerContainer ref={containerRef}>
      {viewerReady && viewerRef.current && <ViewerControls viewer={viewerRef.current} />}
    </ViewerContainer>
  );
};

export default GLViewerWrapper;
